import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@store/authStore';
import { ROUTES } from '@/constants';
import { Skeleton } from '@components/ui/Skeleton';
import ClientCard from './components/ClientCard';
import { computeClientSpend } from './utils';
import { EmptyState } from '@assets/images';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, SCOPES, ACTIONS } from '@/lib/permissions';

import {
  RiSearchLine,
  RiUserAddLine,
  RiArrowDownLine,
  RiDownload2Line,
  RiLoader4Line,
} from '@remixicon/react';

const CLIENTS_PAGE_SIZE = 10;

const Clients = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuthStore();
  const [search, setSearch] = useState('');
  // Debounced mirror of `search` — drives the wire params so we don't
  // fire a request on every keystroke. 400ms matches the /financials
  // and /quotes search-bar cadence.
  const [searchDebounced, setSearchDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // CSV export in-flight — Export button swaps to a spinner and disables
  // while the unpaginated fetch is running.
  const [isExporting, setIsExporting] = useState(false);

  const profileId = userProfile?.userprofile_id;
  const orgId = userProfile?.org_id;

  // Per-role data scope from the permission matrix.
  // self → broker_id: profileId (broker sees own clients)
  // global → broker_org_id: orgId (admin/FC/finance see all clients in the org)
  const { scope: getScope, can, canAccess } = usePermissions();
  const clientsScope = getScope(MODULES.CLIENTS);
  const canAddClient = can(MODULES.CLIENTS, ACTIONS.CREATE);
  // Export is admin-only — gated via the Admin module (admin + super_admin).
  const canExport = canAccess(MODULES.ADMIN);
  const scopeFilter = useMemo(() => {
    if (clientsScope === SCOPES.GLOBAL && orgId) return { broker_org_id: orgId };
    if (clientsScope === SCOPES.SELF && profileId) return { broker_id: profileId };
    return null;
  }, [clientsScope, orgId, profileId]);

  // Shared filter bag for both queries — the summary and list must agree
  // on scope + search so the header count matches the rendered rows.
  const listFilters = useMemo(() => ({
    ...(searchDebounced ? { search: searchDebounced } : {}),
    ...(scopeFilter || {}),
  }), [searchDebounced, scopeFilter]);

  // Server-side paginated list. Load More fetches ONE page (page 0 refires
  // automatically whenever the debounced search or scope changes because
  // both are in the queryKey). No more upfront cascade — matches the
  // /quotes list pattern.
  const {
    data: clientsPages,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['clients', clientsScope, profileId, orgId, searchDebounced],
    queryFn: async ({ pageParam = 0 }) => {
      const data = await apiClient.getClients(pageParam, CLIENTS_PAGE_SIZE, {
        include: 'quotes,quotes.bids',
        ...listFilters,
      });
      return Array.isArray(data) ? data : [];
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const lastCount = Array.isArray(lastPage) ? lastPage.length : 0;
      // Short-page heuristic — BE returns a bare array today with no
      // `has_more` flag, so a page smaller than PAGE_SIZE means we're at
      // the end. If BE later adopts `{ items, has_more }`, flip this.
      if (lastCount < CLIENTS_PAGE_SIZE) return undefined;
      return allPages.length * CLIENTS_PAGE_SIZE;
    },
    enabled: !!scopeFilter,
    staleTime: 30_000,
  });

  const clients = useMemo(() => clientsPages?.pages?.flat() || [], [clientsPages]);

  // Header count — /clients/summary returns { total, filtered_count }.
  // filtered_count respects the search; when search is empty it equals
  // total. Consuming filtered_count unconditionally keeps the header in
  // sync with what the user is filtering to without a case-split.
  const { data: summary } = useQuery({
    queryKey: ['clients-summary', clientsScope, profileId, orgId, searchDebounced],
    queryFn: () => apiClient.getClientsSummary(listFilters),
    enabled: !!scopeFilter,
    staleTime: 30_000,
  });
  const displayCount = summary?.filtered_count;

  // CSV export — fires an unpaginated fetch (server-driven pagination
  // means the in-memory `clients` only reflects the pages already loaded,
  // which is misleading for exports). Reuses the same filter contract
  // as the paginated list so the CSV matches what the user currently
  // sees plus everything below the visible pages.
  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const rowsRaw = await apiClient.getClients(0, 10000, {
        include: 'quotes,quotes.bids',
        ...listFilters,
      });
      const list = Array.isArray(rowsRaw) ? rowsRaw : [];
      if (!list.length) {
        toast.info('No clients match the current filters — nothing to export.');
        return;
      }
      const csvText = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const headers = ['First Name', 'Last Name', 'Company', 'Email', 'Phone', 'Lifetime Value', 'Average Flight Spend'];
      const rows = list.map((c) => {
        const { lifetimeValue, avgFlightSpend } = computeClientSpend(c);
        return [
          csvText(c.first_name),
          csvText(c.last_name),
          csvText(c.company_name || c.company),
          csvText(c.email),
          csvText(c.contact_numbers?.[0] || c.phone),
          lifetimeValue,
          avgFlightSpend,
        ];
      });
      const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `clients-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Clients CSV export failed:', err);
      toast.error('Export failed — try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="pb-20 bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {typeof displayCount === 'number' ? `${displayCount} ` : ''}Clients
        </h1>

        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 sm:flex-none">
            <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 w-full sm:w-68 rounded-full text-sm focus:outline-none"
            />
          </div>

          {/* Filter and Sort by pills removed — they were decorative
              (no onClick) and the ticket didn't add wired filters.
              Re-add once the underlying UX is designed. */}

          {/* Add client — requires Clients module create access (hidden for FC, finance, compliance_qa) */}
          {canAddClient && (
            <button
              onClick={() => navigate(ROUTES.ADD_CLIENT.path)}
              className="flex items-center gap-1.5 px-3 py-2 bg-white text-gray-700 rounded-full text-sm font-medium transition-colors"
            >
              <RiUserAddLine className="w-4 h-4" />
              <span className="hidden sm:inline">Add client</span>
            </button>
          )}

          {/* Export the full filter set to CSV — admin only. Runs an
              unpaginated fetch so exports reflect every match, not just
              the pages already loaded. */}
          {canExport && (
            <button
              onClick={handleExport}
              disabled={isExporting || displayCount === 0}
              className="flex items-center gap-1.5 px-3 py-2 bg-white text-gray-700 rounded-full text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting
                ? <RiLoader4Line className="w-4 h-4 animate-spin" />
                : <RiDownload2Line className="w-4 h-4" />}
              <span className="hidden sm:inline">{isExporting ? 'Exporting…' : 'Export'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Loading — first page only. Subsequent Load More clicks show a
          spinner inside the button instead of blanking the list. */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-60" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-8 w-full" />
            </div>
          ))}
        </div>
      )}

      {/* Empty state — page 0 came back empty. */}
      {!isLoading && clients.length === 0 && (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <img src={EmptyState} alt="No clients" className="w-30 h-30 mx-auto" />
          </div>
          <h3 className="text-md font-semibold text-gray-900 mb-2">
            {searchDebounced ? 'No clients found' : 'No clients enrolled yet'}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {searchDebounced ? 'Try adjusting your search query.' : ''}
          </p>
          {!searchDebounced && (
            <button
              onClick={() => navigate(ROUTES.ADD_CLIENT.path)}
              className="inline-flex items-center gap-1.5 px-4 py-2 !bg-white !border border-primary-500 text-primary-500 rounded-full text-sm font-medium transition-colors hover:bg-gray-800"
            >
              <RiUserAddLine className="w-4 h-4" />
              Add Client
            </button>
          )}
        </div>
      )}

      {/* Client cards grid — server owns filtering + pagination. Render
          every loaded page; "Load More" pulls the next server page. */}
      {!isLoading && clients.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {clients.map((client) => (
              <ClientCard
                key={client.client_id || client.id}
                client={client}
              />
            ))}
          </div>
          {hasNextPage && (
            <div className="flex justify-center mt-8">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="group relative inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm shadow-sm transition-all duration-200 bg-primary-600 text-white hover:bg-primary-700 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {isFetchingNextPage ? (
                  <>
                    <RiLoader4Line className="w-4 h-4 animate-spin" />
                    <span>Loading…</span>
                  </>
                ) : (
                  <>
                    <span>Load More</span>
                    <RiArrowDownLine className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Clients;
